/**
 * Gefahrstoff-Steckbriefe fuer den A+S-Plan.
 *
 * Katalog im Repo, Fassung am Datensatz — Begruendung wie in `ebvKlassen.ts`.
 *
 * ═══ WAS EIN STECKBRIEF LEISTEN MUSS ══════════════════════════════════════
 * Die BG BAU benennt drei wiederkehrende Maengel bei den Stoffeigenschaften
 * (Bauportal 1/2021), und die Felder dieses Katalogs sind die Antwort darauf:
 *
 *  1. „Alle Stoffe werden beschrieben, die in der Analyse in jedweder
 *     Konzentration genannt sind" — deshalb ist die AUSWAHL eine eigene
 *     Angabe am Plan (`StoffAuswahl.hinweis`) und nicht das Abhaken einer
 *     Liste. Ein Stoff gehoert in den Plan, wenn er bei einer Taetigkeit
 *     freigesetzt werden kann, nicht weil er im Laborbericht steht.
 *  2. „Erscheinungsform und Mobilitaet werden nicht dargestellt" — liegt das
 *     PAK-haltige Material als Oel, als Staub oder als Dampf vor? Deshalb
 *     `erscheinungsform` und `mobilitaet` als eigene Felder.
 *  3. „Fehlende Differenzierung innerhalb von Stoffgruppen" — Cyanide
 *     komplex-gebunden oder leicht freisetzbar, Chrom als Cr(III) oder
 *     Cr(VI). Deshalb sind das getrennte Eintraege und keine Sammelposten.
 *
 * ═══ DIE FILTERFRAGE ══════════════════════════════════════════════════════
 * `filter` ist der praktisch folgenreichste Eintrag. Ein pauschales „A2P3
 * fuer alles" ist bequem und in zwei Richtungen falsch: gegen Quecksilber-
 * DAMPF hilft der A2-Anteil nicht (dafuer gibt es Hg-P3), und gegen
 * partikelgebundene PAK ist er ueberfluessig. Der Katalog fuehrt deshalb je
 * Stoff den fachlich richtigen Filter samt Begruendung; `asplanLogic.ts`
 * meldet die Abweichung, statt sie stillschweigend zu uebernehmen.
 */

// 2026-08-23: Grenzwertarten je Stoff eindeutig gemacht (agw_e/agw_a,
// bgw_gebaerfaehig). Ein Bestandsplan traegt weiterhin die Fassung, unter
// der er beurteilt wurde — genau dafuer steht sie am Datensatz.
export const GEFAHRSTOFF_KATALOG_VERSION = '2026-08-23';

/** Woran der Stoff bei der Arbeit tatsaechlich auftritt. */
export type Erscheinungsform = 'staub' | 'oel' | 'fluessig' | 'dampf' | 'faser' | 'gas';

export interface Grenzwert {
    /**
     * 'agw' | 'bgw' | 'bat' | 'blw' | 'akzeptanz' | 'toleranz' | 'vorsorge'
     *
     * ═══ DIE ART MUSS JE STOFF EINDEUTIG SEIN ═════════════════════════════
     * Sie ist der Schluessel, ueber den ein im Plan festgehaltener Wert
     * spaeter wieder seinem Katalogeintrag zugeordnet wird. Fuehrt ein Stoff
     * zweimal dieselbe Art, gewinnt der zuerst gefundene Eintrag — und die
     * Pruefung meldete dem Bearbeiter, „der Katalog fuehrt inzwischen
     * 10 mg/m³", waehrend im Plan der A-Staub-Wert 1,25 mg/m³ stand. Wer das
     * befolgt, verachtfacht seinen Grenzwert.
     *
     * Deshalb werden Werte, die sich nur in ihrem Geltungsbereich
     * unterscheiden, in der Art getrennt: 'agw_e'/'agw_a' fuer E- und
     * A-Staub, 'bgw_gebaerfaehig' fuer den engeren Blei-Wert. Wer einen Wert
     * ergaenzt, prueft zuerst, ob die Art bei diesem Stoff schon vergeben
     * ist.
     */
    art: string;
    wert: number;
    einheit: string;
    quelle: string;
    /** Fassung der Quelle. Wird beim Uebernehmen in den Plan mitkopiert. */
    fassung: string;
    hinweis?: string;
    /** Ein Wert, der nur noch nachrichtlich gefuehrt werden darf. Der Plan
     *  darf ihn zeigen, aber nicht als Massstab verwenden. */
    veraltet?: boolean;
}

export interface BiomonitoringParameter {
    parameter: string;
    medium: 'urin' | 'blut';
    wert?: number;
    einheit?: string;
    quelle: string;
    zeitpunkte: string;
}

export interface Gefahrstoff {
    key: string;
    name: string;
    synonyme: string[];
    gruppe: string;
    erscheinungsform: Erscheinungsform[];
    mobilitaet: {
        fluechtig: boolean;
        staubgaengig: boolean;
        hautresorptiv: boolean;
        /** Fuer die Frage, ob Eluatwerte ueberhaupt eine Rolle spielen. */
        wasserloeslich: boolean;
    };
    /** CMR-Einstufung im Klartext; leer, wenn keine. */
    cmr: string;
    wirkung: string;
    symptome: string;
    aufnahmewege: string;
    grenzwerte: Grenzwert[];
    biomonitoring: BiomonitoringParameter[];
    filter: {
        empfohlen: string;
        begruendung: string;
        /** Filter, die haeufig gewaehlt werden, hier aber nicht passen. */
        haeufig_falsch?: string;
    };
    /** Die Stelle, an der bei diesem Stoff erfahrungsgemaess etwas schiefgeht. */
    hinweis: string;
    veraltet?: boolean;
}

export const GEFAHRSTOFFE: Gefahrstoff[] = [
    {
        key: 'pak16',
        name: 'Polycyclische Aromatische Kohlenwasserstoffe (Summe 16 PAK)',
        synonyme: ['PAK', 'PAK16', 'EPA-PAK'],
        gruppe: 'organisch',
        erscheinungsform: ['staub', 'oel'],
        mobilitaet: { fluechtig: false, staubgaengig: true, hautresorptiv: true, wasserloeslich: false },
        cmr: 'Benzo[a]pyren krebserzeugend Kat. 1B, keimzellmutagen, reproduktionstoxisch',
        wirkung: 'Reizung von Atemwegen, Augen, Haut und Verdauungsorganen; Leber- und '
            + 'Nierenschäden; Blutbildveränderungen. Leitkomponente der krebserzeugenden '
            + 'Wirkung ist Benzo[a]pyren.',
        symptome: 'Hautreizung, Photosensibilisierung, Reizhusten, Augenbrennen',
        aufnahmewege: 'inhalativ über partikelgebundene Stäube und Aerosole, dermal über '
            + 'Hautkontakt mit belastetem Material, oral über Hand-zu-Mund-Kontakt',
        grenzwerte: [
            {
                art: 'akzeptanz', wert: 70, einheit: 'ng/m³',
                quelle: 'TRGS 910 (Benzo[a]pyren)', fassung: 'TRGS 910',
                hinweis: 'Risikobezogenes Maßnahmenkonzept; unterhalb gilt das Risiko als akzeptabel.',
            },
            {
                art: 'toleranz', wert: 700, einheit: 'ng/m³',
                quelle: 'TRGS 910 (Benzo[a]pyren)', fassung: 'TRGS 910',
                hinweis: 'Ueberschreitung: sofortige Maßnahmen, Arbeiten unterbrechen.',
            },
            {
                art: 'agw', wert: 0.002, einheit: 'mg/m³',
                quelle: 'TRK Benzo[a]pyren, früher TRGS 551', fassung: 'ueberholt',
                veraltet: true,
                hinweis: 'Das TRK-Konzept ist entfallen. Nur nachrichtlich führen — Maßstab '
                    + 'sind Akzeptanz- und Toleranzkonzentration nach TRGS 910.',
            },
        ],
        biomonitoring: [{
            parameter: '1-Hydroxypyren (1-OHP)', medium: 'urin',
            quelle: 'TRGS 903 / EKA-Wert nach DFG',
            zeitpunkte: 'Schichtende nach mehreren Expositionsschichten',
        }],
        filter: {
            empfohlen: 'P3',
            begruendung: 'PAK liegen im Boden partikelgebunden vor; maßgeblich ist der '
                + 'Partikelfilter. Ein A2-Anteil ist nur bei nachgewiesenen flüchtigen '
                + 'Begleitstoffen (BTEX, Naphthalin) erforderlich.',
            haeufig_falsch: 'A2P3 ohne Nachweis flüchtiger Begleitstoffe',
        },
        hinweis: 'Hautresorptiv — der Hautschutzplan ist hier keine Formalie. Bei anhaftendem '
            + 'Material ist die Schutzkleidung unverzüglich zu wechseln, nicht abzubürsten.',
    },
    {
        key: 'bap',
        name: 'Benzo[a]pyren',
        synonyme: ['B[a]P'],
        gruppe: 'organisch',
        erscheinungsform: ['staub'],
        mobilitaet: { fluechtig: false, staubgaengig: true, hautresorptiv: true, wasserloeslich: false },
        cmr: 'krebserzeugend Kat. 1B, keimzellmutagen Kat. 1B, reproduktionstoxisch Kat. 1B',
        wirkung: 'Leitkomponente der PAK-Bewertung; krebserzeugend, fruchtschädigend.',
        symptome: 'siehe PAK',
        aufnahmewege: 'inhalativ, dermal, oral',
        grenzwerte: [
            { art: 'akzeptanz', wert: 70, einheit: 'ng/m³', quelle: 'TRGS 910', fassung: 'TRGS 910' },
            { art: 'toleranz', wert: 700, einheit: 'ng/m³', quelle: 'TRGS 910', fassung: 'TRGS 910' },
        ],
        biomonitoring: [],
        filter: { empfohlen: 'P3', begruendung: 'partikelgebunden' },
        hinweis: 'Wird als Leitkomponente gemessen, nicht als Einzelstoff bewertet. '
            + 'Probenahme und Analytik nach aktuellem IFA-Verfahren.',
    },
    {
        key: 'hg',
        name: 'Quecksilber und anorganische Verbindungen',
        synonyme: ['Hg'],
        gruppe: 'metall',
        erscheinungsform: ['staub', 'dampf', 'fluessig'],
        mobilitaet: { fluechtig: true, staubgaengig: true, hautresorptiv: true, wasserloeslich: true },
        cmr: '',
        wirkung: 'Reichert sich im Körper an, greift das Nervensystem an, schädigt Mund und '
            + 'Atemwege. Chronische Vergiftung mit Kopfschmerz, Schwindel, Müdigkeit, '
            + 'Uebelkeit, Defekten der Blutbildung und der Muskulatur.',
        symptome: 'Metallgeschmack, Zahnfleischentzündung, Tremor, Seh- und Hörstörungen',
        aufnahmewege: 'inhalativ über Staub UND Dampf, dermal, oral',
        grenzwerte: [
            { art: 'agw', wert: 0.02, einheit: 'mg/m³', quelle: 'TRGS 900', fassung: 'TRGS 900' },
        ],
        biomonitoring: [{
            parameter: 'Quecksilber im Urin (Hg-U)', medium: 'urin',
            wert: 25, einheit: 'µg/g Kreatinin', quelle: 'BAT-Wert TRGS 903',
            zeitpunkte: 'vor Arbeitsbeginn (Baseline), nach 4 Wochen Exposition, nach Arbeitsende',
        }],
        filter: {
            empfohlen: 'Hg-P3',
            begruendung: 'Gegen Quecksilber-DAMPF wirkt nur der spezielle Quecksilberfilter. '
                + 'Liegt Hg ausschließlich staubgebunden vor, genügt P3.',
            haeufig_falsch: 'A2P3 — der A2-Anteil bindet keinen Quecksilberdampf',
        },
        hinweis: 'Dampffreisetzung steigt mit der Temperatur: freigelegtes belastetes Material '
            + 'in der Sonne ist etwas anderes als dasselbe Material im Aushub. Die '
            + 'Umgebungsbedingungen des Arbeitsbereichs gehören deshalb in die Beurteilung.',
    },
    {
        key: 'pb',
        name: 'Blei und anorganische Verbindungen',
        synonyme: ['Pb'],
        gruppe: 'metall',
        erscheinungsform: ['staub'],
        mobilitaet: { fluechtig: false, staubgaengig: true, hautresorptiv: false, wasserloeslich: false },
        cmr: 'anorganische Bleiverbindungen reproduktionstoxisch',
        wirkung: 'Anreicherung in Knochen, Blut und Weichgeweben. Kopfschmerz, Müdigkeit, '
            + 'Bauchkoliken, Anämie; bei längerer Belastung Schäden an Nervensystem, '
            + 'Blutbildung und Nieren.',
        symptome: 'Bauchkoliken, Blässe, Müdigkeit, periphere Nervenstörungen',
        aufnahmewege: 'inhalativ über Staub, oral über Hand-zu-Mund-Kontakt; eine relevante '
            + 'Aufnahme über die Haut besteht NICHT',
        grenzwerte: [
            {
                art: 'bgw', wert: 150, einheit: 'µg/l Vollblut', quelle: 'TRGS 903', fassung: 'TRGS 903',
                hinweis: 'Für gebärfähige Frauen gilt der niedrigere Wert von 100 µg/l.',
            },
            {
                art: 'bgw_gebaerfaehig', wert: 100, einheit: 'µg/l Vollblut (gebärfähige Frauen)',
                quelle: 'TRGS 903', fassung: 'TRGS 903',
            },
        ],
        biomonitoring: [{
            parameter: 'Blei im Vollblut (Pb-B)', medium: 'blut',
            wert: 150, einheit: 'µg/l', quelle: 'BGW TRGS 903',
            zeitpunkte: 'vor Arbeitsbeginn (Baseline), nach 4 Wochen Exposition, nach Arbeitsende',
        }],
        filter: {
            empfohlen: 'P3',
            begruendung: 'rein partikulär — ein Gasfilteranteil hat hier keine Funktion',
        },
        hinweis: 'Es gibt keinen Luft-AGW; beurteilt wird über den biologischen Grenzwert im '
            + 'Blut. Die Beschäftigungsbeschränkung für gebärfähige Frauen ist bei Blei '
            + 'strenger als bei den übrigen Stoffen dieses Katalogs.',
    },
    {
        key: 'mkw',
        name: 'Mineralölkohlenwasserstoffe (MKW)',
        synonyme: ['KW', 'Kohlenwasserstoffe C10-C40'],
        gruppe: 'organisch',
        erscheinungsform: ['oel', 'fluessig', 'dampf'],
        mobilitaet: { fluechtig: true, staubgaengig: false, hautresorptiv: true, wasserloeslich: false },
        cmr: '',
        wirkung: 'Reizung von Haut und Atemwegen, entfettende Wirkung; je nach Zusammensetzung '
            + 'narkotische Wirkung der leichtflüchtigen Anteile.',
        symptome: 'Hautaustrocknung, Kopfschmerz, Schwindel bei hohen Konzentrationen',
        aufnahmewege: 'inhalativ über Dämpfe und Aerosole, dermal',
        grenzwerte: [],
        biomonitoring: [],
        filter: {
            empfohlen: 'A2P3',
            begruendung: 'Der Gasfilteranteil ist hier begründet: MKW enthalten flüchtige '
                + 'Anteile, die ein Partikelfilter nicht zurückhält.',
        },
        hinweis: 'Summenparameter — „MKW“ allein sagt nichts über die Flüchtigkeit. Die BG BAU '
            + 'nennt genau solche Sammelangaben ohne Einzelstoffbetrachtung als Mangel. '
            + 'Kettenlängenverteilung aus dem Prüfbericht übernehmen.',
    },
    {
        key: 'btex',
        name: 'BTEX (Benzol, Toluol, Ethylbenzol, Xylole)',
        synonyme: ['Aromaten'],
        gruppe: 'organisch',
        erscheinungsform: ['fluessig', 'dampf'],
        mobilitaet: { fluechtig: true, staubgaengig: false, hautresorptiv: true, wasserloeslich: true },
        cmr: 'Benzol krebserzeugend Kat. 1A, keimzellmutagen',
        wirkung: 'Benzol schädigt das blutbildende System (Leukämierisiko); die übrigen '
            + 'Komponenten wirken narkotisch und reizend.',
        symptome: 'Kopfschmerz, Schwindel, Benommenheit, Schleimhautreizung',
        aufnahmewege: 'inhalativ über Dämpfe, dermal',
        grenzwerte: [
            {
                art: 'akzeptanz', wert: 0.2, einheit: 'mg/m³ (Benzol)',
                quelle: 'TRGS 910', fassung: 'TRGS 910',
            },
        ],
        biomonitoring: [],
        filter: {
            empfohlen: 'A2P3',
            begruendung: 'flüchtig — der Gasfilteranteil ist zwingend, ein reiner P3 wirkungslos',
        },
        hinweis: 'Führt zusammen mit Deponie- und Bodenluft zur Explosionsfrage: bei Verdacht '
            + 'vorlaufende Messung mit Mehrfachgaswarngerät (UEG, O2, CO, H2S) und '
            + 'Explosionsschutzdokument nach BetrSichV prüfen.',
    },
    {
        key: 'asbest',
        name: 'Asbest',
        synonyme: [],
        gruppe: 'faser',
        erscheinungsform: ['faser'],
        mobilitaet: { fluechtig: false, staubgaengig: true, hautresorptiv: false, wasserloeslich: false },
        cmr: 'krebserzeugend Kat. 1A',
        wirkung: 'Asbestose, Lungen- und Kehlkopfkrebs, Mesotheliom. Wirkung erst nach '
            + 'Jahrzehnten; keine akute Warnwirkung.',
        symptome: 'keine akuten Symptome — das ist die eigentliche Gefahr',
        aufnahmewege: 'ausschließlich inhalativ über Fasern',
        grenzwerte: [
            {
                art: 'akzeptanz', wert: 10000, einheit: 'Fasern/m³',
                quelle: 'TRGS 910 / TRGS 519', fassung: 'TRGS 910',
            },
        ],
        biomonitoring: [],
        filter: {
            empfohlen: 'P3 (Vollmaske oder Gebläse TM3P)',
            begruendung: 'Faserstaub — Partikelfilter höchster Klasse, dichtsitzender Anschluss',
        },
        hinweis: 'Für Asbest gilt die TRGS 519 mit EIGENEN Anforderungen (Sachkunde, Anzeige, '
            + 'Schwarz-Weiß-Anlage mit Unterdruckhaltung). Dieses Modul ersetzt sie NICHT — '
            + 'trifft man Asbest an, ist das ein Anlass zum Stopp und zur Neubewertung, nicht '
            + 'ein weiterer Eintrag in der Stoffliste.',
    },
    {
        key: 'kmf',
        name: 'Künstliche Mineralfasern (alte KMF)',
        synonyme: ['Mineralwolle'],
        gruppe: 'faser',
        erscheinungsform: ['faser'],
        mobilitaet: { fluechtig: false, staubgaengig: true, hautresorptiv: false, wasserloeslich: false },
        cmr: 'alte KMF (vor 1996) krebsverdächtig',
        wirkung: 'Reizung von Haut, Augen und Atemwegen; bei alten Fasern Krebsverdacht.',
        symptome: 'Juckreiz, Hautrötung, Reizhusten',
        aufnahmewege: 'inhalativ, mechanische Hautreizung',
        grenzwerte: [],
        biomonitoring: [],
        filter: { empfohlen: 'P2/P3', begruendung: 'Faser- und Staubfilter' },
        hinweis: 'Maßgeblich ist das Einbaujahr: nach 1996 hergestellte Fasern gelten als '
            + 'biolöslich. Ohne Herkunftsnachweis vom ungünstigeren Fall ausgehen.',
    },
    {
        key: 'cr6',
        name: 'Chrom(VI)-Verbindungen',
        synonyme: ['Chromat'],
        gruppe: 'metall',
        erscheinungsform: ['staub'],
        mobilitaet: { fluechtig: false, staubgaengig: true, hautresorptiv: true, wasserloeslich: true },
        cmr: 'krebserzeugend Kat. 1A/1B, sensibilisierend',
        wirkung: 'Krebserzeugend beim Einatmen; starke Sensibilisierung der Haut '
            + '(Chromatekzem), Schleimhautschäden bis zur Septumperforation.',
        symptome: 'Ekzem, Nasenbluten, Reizhusten',
        aufnahmewege: 'inhalativ, dermal',
        grenzwerte: [
            { art: 'akzeptanz', wert: 1, einheit: 'µg/m³', quelle: 'TRGS 910', fassung: 'TRGS 910' },
        ],
        biomonitoring: [],
        filter: { empfohlen: 'P3', begruendung: 'partikulaer' },
        hinweis: 'NICHT mit Gesamt-Chrom verwechseln: Cr(III) ist vergleichsweise harmlos, '
            + 'Cr(VI) krebserzeugend. Ein Laborwert „Chrom“ ohne Oxidationsstufe ist für den '
            + 'Arbeitsschutz unbrauchbar — genau der von der BG BAU benannte Mangel.',
    },
    {
        key: 'cn_leicht',
        name: 'Cyanide, leicht freisetzbar',
        synonyme: ['CN leicht freisetzbar'],
        gruppe: 'anorganisch',
        erscheinungsform: ['staub', 'gas'],
        mobilitaet: { fluechtig: true, staubgaengig: true, hautresorptiv: true, wasserloeslich: true },
        cmr: '',
        wirkung: 'Blockiert die Zellatmung. Akut lebensgefährlich; Blausäure entsteht bei '
            + 'Kontakt mit Säuren.',
        symptome: 'Kopfschmerz, Schwindel, Atemnot, Bewusstlosigkeit — sehr rascher Verlauf',
        aufnahmewege: 'inhalativ, dermal, oral',
        grenzwerte: [],
        biomonitoring: [],
        filter: { empfohlen: 'B-P3 / Isoliergerät', begruendung: 'Blausäure erfordert Gasfiltertyp B; '
            + 'bei unklarer Konzentration umluftunabhängiges Gerät' },
        hinweis: 'AKUTGEFAHR — hier ist die kontinuierliche Messung mit Warngerät Pflicht '
            + '(Messziel „akut“ nach TRGS 524 Anlage 9). Komplex gebundene Cyanide (z. B. '
            + 'Berliner Blau) sind davon zu unterscheiden und deutlich weniger gefährlich; '
            + 'ein Laborwert „Cyanid gesamt“ trennt das NICHT.',
    },
    {
        key: 'mineralstaub',
        name: 'Mineralischer Staub (A- und E-Staub)',
        synonyme: ['Quarzfeinstaub'],
        gruppe: 'staub',
        erscheinungsform: ['staub'],
        mobilitaet: { fluechtig: false, staubgaengig: true, hautresorptiv: false, wasserloeslich: false },
        cmr: 'Quarzfeinstaub (alveolengängig) krebserzeugend',
        wirkung: 'Silikose, chronische Bronchitis; alveolengängiger Quarzstaub ist '
            + 'krebserzeugend.',
        symptome: 'Husten, Atemnot bei Belastung',
        aufnahmewege: 'inhalativ',
        grenzwerte: [
            { art: 'agw_e', wert: 10, einheit: 'mg/m³ (E-Staub)', quelle: 'TRGS 900', fassung: 'TRGS 900' },
            { art: 'agw_a', wert: 1.25, einheit: 'mg/m³ (A-Staub)', quelle: 'TRGS 900', fassung: 'TRGS 900' },
        ],
        biomonitoring: [],
        filter: { empfohlen: 'P2/P3', begruendung: 'Partikelfilter nach Staubart und Konzentration' },
        hinweis: 'Staub ist im kontaminierten Bereich doppelt bedeutsam: eigene Wirkung UND '
            + 'Träger der übrigen Schadstoffe. Deshalb taugt er als Leitparameter der '
            + 'laufenden Ueberwachung, auch wenn der eigentliche Zielstoff ein anderer ist.',
    },
];

const NACH_KEY = new Map(GEFAHRSTOFFE.map(s => [s.key, s]));

export const gefahrstoff = (key: string): Gefahrstoff | null => NACH_KEY.get(key) ?? null;

/** Alle Stoffe, die bei mindestens einer Erscheinungsform fluechtig sind —
 *  nur fuer sie ist ein Gasfilteranteil (A2, B …) begruendet. */
export const istFluechtig = (key: string): boolean => !!NACH_KEY.get(key)?.mobilitaet.fluechtig;

/** Alle hautresorptiven Stoffe. Fuer sie ist der Hautschutzplan Pflicht,
 *  nicht Kür. */
export const istHautresorptiv = (key: string): boolean =>
    !!NACH_KEY.get(key)?.mobilitaet.hautresorptiv;

/** Grenzwert einer bestimmten Art; `null`, wenn der Stoff keinen fuehrt.
 *  Als VERALTET markierte Werte werden dabei uebergangen — sie duerfen im
 *  Plan erscheinen, aber nie als Massstab dienen. */
export const grenzwert = (key: string, art: string): Grenzwert | null =>
    NACH_KEY.get(key)?.grenzwerte.find(g => g.art === art && !g.veraltet) ?? null;

/** Was der Katalog zu einem im Plan festgehaltenen Wert sagt. */
export interface KatalogZuordnung {
    /** Der zugehoerige Katalogeintrag; `null`, wenn keiner sicher zuzuordnen ist. */
    treffer: Grenzwert | null;
    /**
     * Die Art passt zu mehreren Katalogeintraegen, und die Einheit trennt sie
     * nicht. Dann wird KEINER genommen: ein falsch zugeordneter Grenzwert ist
     * schlimmer als ein nicht zugeordneter, weil er als Aufforderung
     * erscheint, den Wert im Plan anzupassen.
     */
    mehrdeutig: boolean;
}

/**
 * Ordnet einen im Plan gespeicherten Grenzwert seinem Katalogeintrag zu.
 *
 * ═══ WARUM DAS MEHR IST ALS EIN `find` ════════════════════════════════════
 * Bestandsdaten tragen die Art, die zum Zeitpunkt der Uebernahme galt. Als
 * der Katalog fuer mineralischen Staub noch zweimal 'agw' fuehrte, ist genau
 * das gespeichert worden. Wuerde jetzt nur noch auf 'agw_e'/'agw_a' geprueft,
 * faende ein solcher Eintrag gar nichts mehr — die Pruefung schwiege, und
 * Schweigen liest sich wie „in Ordnung".
 *
 * Deshalb zwei Stufen: erst die Art genau, dann die feiner gewordenen Arten
 * derselben Familie ('agw' -> 'agw_e', 'agw_a'), getrennt ueber die Einheit,
 * die beim Uebernehmen wortgleich mitkopiert wird. Bleiben mehrere uebrig,
 * wird nichts behauptet, sondern `mehrdeutig` gemeldet.
 */
export const katalogGrenzwert = (
    stoffKey: string,
    art: string,
    einheit: string,
): KatalogZuordnung => {
    const alle = NACH_KEY.get(stoffKey)?.grenzwerte ?? [];

    const genau = alle.filter(g => g.art === art);
    if (genau.length === 1) return { treffer: genau[0], mehrdeutig: false };
    if (genau.length > 1) {
        // Sollte der Katalog kuenftig doch wieder zwei gleiche Arten fuehren,
        // faellt das hier auf, statt still den ersten zu nehmen.
        const ueberEinheit = genau.filter(g => g.einheit === einheit);
        return ueberEinheit.length === 1
            ? { treffer: ueberEinheit[0], mehrdeutig: false }
            : { treffer: null, mehrdeutig: true };
    }

    const verfeinert = alle.filter(g => g.art.startsWith(`${art}_`));
    if (verfeinert.length === 0) return { treffer: null, mehrdeutig: false };
    const ueberEinheit = verfeinert.filter(g => g.einheit === einheit);
    return ueberEinheit.length === 1
        ? { treffer: ueberEinheit[0], mehrdeutig: false }
        : { treffer: null, mehrdeutig: true };
};
